import { Link } from 'react-router-dom';
import { Clock, Users, ChefHat, ArrowRight } from 'lucide-react';
import Navbar from '@/components/site/Navbar';
import Footer from '@/components/site/Footer';
import Seo from '@/components/Seo';
import { RECIPES } from '@/content/recipes';

/**
 * Recipe index — chronological grid, richest cards up top.
 * The homepage sends buyers here as a discovery funnel; every card links to
 * a full recipe with a "buy the star ingredient" cross-link to /order.
 */
export default function RecipesIndex() {
  const sorted = [...RECIPES].sort((a, b) => b.dateISO.localeCompare(a.dateISO));

  return (
    <div className="section-light App relative">
      <Seo
        title="Recipes · Cook with Premium Australian Meat"
        description="Chef-tested recipes for premium Australian beef, lamb, poultry and smallgoods — Wagyu ragu, Halal lamb slow-roast, Malaysian rendang, tomahawk salsa verde and more. Halal-certified ingredients, every time."
        keywords="beef recipes Australia, halal recipes, lamb recipes, wagyu recipes, rendang recipe, tomahawk recipe, meat recipes Perth"
        path="/recipes"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'The Meat Works Recipe Collection',
          description: 'A curated library of chef-tested recipes for Australian Halal-certified beef, lamb, poultry and smallgoods.',
          url: 'https://themeatworks.com.au/recipes',
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-14 bg-gradient-to-b from-[#FDFBF7] via-[#F7F3EC] to-[#FAFAFA]">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 text-center">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-4">
            RECIPES &middot; FROM OUR KITCHEN TO YOURS
          </div>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-black">
            Cook with meat
            <br />
            <span className="italic text-[var(--mw-primary)]">that&apos;s worth cooking.</span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-base text-black/60 leading-relaxed">
            Chef-tested recipes built around premium Halal-certified Australian beef, lamb, poultry and
            house-cured smallgoods — every one linked to the exact cut we&apos;d send you.
          </p>
        </div>
      </section>

      {/* Grid */}
      <main className="bg-[#FAFAFA] pb-24">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {sorted.map((r, i) => (
              <Link
                key={r.slug}
                to={`/recipes/${r.slug}`}
                data-testid={`recipe-card-${r.slug}`}
                className="group block bg-white border border-black/[0.06] hover:border-black/25 transition-colors"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-black/5">
                  <img
                    src={r.heroImage}
                    alt={r.title}
                    loading={i < 3 ? 'eager' : 'lazy'}
                    fetchpriority={i === 0 ? 'high' : 'auto'}
                    decoding="async"
                    width="480"
                    height="600"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                  />
                </div>
                <div className="p-6">
                  <div className="text-[9px] uppercase tracking-[0.28em] text-[var(--mw-primary)] mb-2">
                    {r.cuisine}   &middot;   {r.difficulty}
                  </div>
                  <h2 className="font-display text-xl sm:text-2xl leading-tight text-black mb-2 group-hover:text-[var(--mw-primary)] transition-colors">
                    {r.title}
                  </h2>
                  <p className="text-sm text-black/60 leading-relaxed line-clamp-3">{r.tagline}</p>
                  <div className="mt-5 flex items-center gap-4 text-[10px] uppercase tracking-[0.2em] text-black/45">
                    <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {Math.round((r.prepMinutes + r.cookMinutes) / 60) || '<1'}h</span>
                    <span className="inline-flex items-center gap-1.5"><Users size={12} /> {r.yield}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
